/**
 * Ecosystem Types
 * 
 * Types for the Training Ecosystem Integration layer:
 * organizations (via training_providers), event series, event instances,
 * cross-domain source type mapping, affiliations, and form data types.
 */

// ── Organization Classification ───────────────────────────────
export type OrgKind =
  | 'alliance'
  | 'association'
  | 'state_association'
  | 'trade_show_producer'
  | 'supplier_association'
  | 'standards_body'
  | 'educator_community'
  | 'training_provider'
  | 'distributor'
  | 'publisher'
  | 'platform'
  | 'other';

export const ORG_KIND_LABELS: Record<OrgKind, string> = {
  alliance: 'Alliance',
  association: 'Association',
  state_association: 'State Association',
  trade_show_producer: 'Trade Show Producer',
  supplier_association: 'Supplier Association',
  standards_body: 'Standards Body',
  educator_community: 'Educator Community',
  training_provider: 'Training Provider',
  distributor: 'Distributor',
  publisher: 'Publisher',
  platform: 'Platform',
  other: 'Other',
};

// ── Priority Tier ─────────────────────────────────────────────
export type PriorityTier = 1 | 2 | 3;

export const PRIORITY_TIER_LABELS: Record<PriorityTier, string> = {
  1: 'Tier 1 — Core Partners',
  2: 'Tier 2 — Active Network',
  3: 'Tier 3 — Monitored',
};

// ── Event Frequency ───────────────────────────────────────────
export type EventFrequency = 'annual' | 'quarterly' | 'monthly' | 'webinar_series' | 'irregular';

export const EVENT_FREQUENCY_LABELS: Record<EventFrequency, string> = {
  annual: 'Annual',
  quarterly: 'Quarterly',
  monthly: 'Monthly',
  webinar_series: 'Webinar Series',
  irregular: 'Irregular',
};

// ── Source Types (ingestion domain) ───────────────────────────
export type EcosystemSourceType = 'manual' | 'calendar_ics' | 'rss_feed' | 'json_api' | 'partner_feed';

export const ECOSYSTEM_SOURCE_TYPE_LABELS: Record<EcosystemSourceType, string> = {
  manual: 'Manual Entry',
  calendar_ics: 'Calendar (ICS)',
  rss_feed: 'RSS Feed',
  json_api: 'JSON API',
  partner_feed: 'Partner Feed',
};

/**
 * Cross-domain source type mapping
 * Maps provider discovery source types → ingestion source types
 */
export const SOURCE_TYPE_MAP = {
  discovery_to_ingestion: {
    calendar_url: 'calendar_ics',
    rss_feed: 'rss_feed',
    json_api: 'json_api',
  },
} as const;

// ── Affiliation / Graph-Ready Relationships ──────────────────
export type AffiliationRelationship =
  | 'appears_at'
  | 'teaches_for'
  | 'partner_of'
  | 'published_on'
  | 'member_of'
  | 'subsidiary_of';

export const AFFILIATION_LABELS: Record<AffiliationRelationship, string> = {
  appears_at: 'Appears At',
  teaches_for: 'Teaches For',
  partner_of: 'Partner Of',
  published_on: 'Published On',
  member_of: 'Member Of',
  subsidiary_of: 'Subsidiary Of',
};

export interface ProviderAffiliation {
  provider_id: string;
  relationship_type: AffiliationRelationship;
  display_name?: string;
  notes?: string;
}

/**
 * Provider metadata contract — shape of training_providers.provider_metadata
 * when used for ecosystem purposes.
 */
export interface ProviderMetadataContract {
  affiliations?: ProviderAffiliation[];
  [key: string]: unknown;
}

// ── Stable Key Naming Conventions ────────────────────────────
export const STABLE_KEY_PREFIXES = {
  organization: 'org',
  platform: 'platform',
  domain: 'domain',
  provider: 'provider',
  creator: 'creator',
  series: 'series',
  event: 'event',
} as const;

// ── Ecosystem Provider Fields (additive to TrainingProviderExtended) ──
export interface TrainingProviderEcosystemFields {
  stable_key: string | null;
  org_kind: OrgKind | null;
  coverage: string | null;
  parent_provider_id: string | null;
  priority_tier: PriorityTier | null;
  canonical_domain: string | null;
}

// ── Event Series ──────────────────────────────────────────────
export interface TrainingEventSeries {
  id: string;
  stable_key: string;
  series_name: string;
  organizer_provider_id: string;
  frequency: EventFrequency;
  default_audience: string[];
  default_tracks: string[];
  website_url: string | null;
  notes: string | null;
  is_active: boolean;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  organizer?: {
    id: string;
    display_name: string;
    stable_key: string | null;
  };
}

// ── Event Instance ────────────────────────────────────────────
export interface TrainingEventInstance {
  id: string;
  stable_key: string;
  series_id: string;
  start_date: string | null;
  end_date: string | null;
  timezone: string | null;
  venue_name: string | null;
  city: string | null;
  state: string | null;
  country: string;
  event_url: string | null;
  registration_url: string | null;
  audience: string[];
  tracks: string[];
  source_type: EcosystemSourceType;
  source_url: string | null;
  source_last_checked_at: string | null;
  source_hash: string | null;
  is_active: boolean;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  series?: TrainingEventSeries;
}

// ── Event↔Artifact Join ──────────────────────────────────────
export interface TrainingEventArtifact {
  id: string;
  event_instance_id: string;
  artifact_id: string;
  offering_instance_id: string | null;
  session_code: string | null;
  room_or_track: string | null;
  session_title_override: string | null;
  instructor_name_override: string | null;
  created_at: string;
}

// ── Provider Contact (admin-only) ─────────────────────────────
export interface TrainingProviderContact {
  id: string;
  provider_id: string;
  role: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  is_public: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// ── Form Data Types ──────────────────────────────────────────
export interface EcosystemEntityFormData {
  display_name: string;
  org_kind: OrgKind;
  priority_tier: PriorityTier | null;
  coverage: string;
  parent_provider_id: string | null;
  canonical_domain: string;
  website_url: string;
  description: string;
  logo_url: string;
  stable_key: string;
  provider_type: string;
}

export interface EventSeriesFormData {
  series_name: string;
  organizer_provider_id: string;
  frequency: EventFrequency;
  default_audience: string;
  default_tracks: string;
  website_url: string;
  notes: string;
  stable_key: string;
}

export interface EventInstanceFormData {
  series_id: string;
  start_date: string;
  end_date: string;
  timezone: string;
  venue_name: string;
  city: string;
  state: string;
  country: string;
  event_url: string;
  registration_url: string;
  audience: string;
  tracks: string;
  source_type: EcosystemSourceType;
  stable_key: string;
}

export interface LinkArtifactToEventData {
  event_instance_id: string;
  artifact_id: string;
  offering_instance_id?: string;
  session_code: string;
  room_or_track: string;
}

export interface LinkOfferingToEventData {
  event_instance_id: string;
  offering_instance_id: string;
  session_code: string;
}

// ── Entity Relationships (multi-path graph) ──────────────────
export type EntityRelationshipType = 'belongs_to' | 'publishes_on' | 'teaches_for' | 'hosted_by';

export const RELATIONSHIP_TYPE_LABELS: Record<EntityRelationshipType, string> = {
  belongs_to: 'Belongs To',
  publishes_on: 'Publishes On',
  teaches_for: 'Teaches For',
  hosted_by: 'Hosted By',
};

export const RELATIONSHIP_TYPE_ICONS: Record<EntityRelationshipType, string> = {
  belongs_to: '',
  publishes_on: '📡',
  teaches_for: '🎓',
  hosted_by: '🏛',
};

export interface EntityRelationship {
  id: string;
  parent_id: string;
  child_id: string;
  relationship_type: EntityRelationshipType;
  is_primary: boolean;
  needs_review: boolean;
  created_at: string;
  parent_name?: string;
  child_name?: string;
}

// ── Entity Detail (for detail panel queries) ─────────────────
export interface EcosystemEntityDetail {
  childProviders: Array<{ id: string; display_name: string; org_kind: string | null; stable_key: string | null }>;
  sources: Array<{ id: string; source_type: string; source_identifier: string; source_name: string }>;
  offerings: Array<{ id: string; start_datetime: string | null; end_datetime: string | null; delivery_type: string | null; status: string }>;
  eventSeries: Array<TrainingEventSeries>;
  artifacts: Array<{ id: string; title: string | null; artifact_type: string; provider_name: string | null }>;
  affiliations: ProviderAffiliation[];
  relationships: {
    asParent: EntityRelationship[];
    asChild: EntityRelationship[];
  };
  counts: {
    providers: number;
    sources: number;
    offerings: number;
    eventSeries: number;
    artifacts: number;
    affiliations: number;
    relationships: number;
  };
}
